import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import {
  useGetEngineExplanationQuery,
  useGetLatestEngineExplanationQuery,
  useRequestEngineExplanationMutation,
} from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IEngineExplanation,
} from "types/BenefitComparison/BenefitComparison";

import ChatMarkdown from "../ContractModelDrafts/wizard/chatMarkdown";
import { relativeTime } from "./remitAccuracyWording";

export const EXPLAIN_BUTTON_LABEL = "Ask Cyrus to Explain";
export const ASK_AGAIN_LABEL = "Ask again";
export const STALE_TEXT =
  "The engine's settings changed since Cyrus wrote this.";
export const WORKING_TEXT = "Cyrus is looking into it… (usually 1–3 minutes)";
/** How often the panel re-reads a QUEUED / RUNNING explanation. */
export const POLL_MS = 4000;
/** Past this, say the wait is unusual — on dev there is no worker at all. */
export const SLOW_AFTER_MS = 15 * 60 * 1000;

const isActive = (explanation: IEngineExplanation | null | undefined) =>
  explanation?.status === "QUEUED" || explanation?.status === "RUNNING";

/**
 * "Ask Cyrus to Explain" — under the selected check's response on the cluster page.
 *
 * One click queues a question for Cyrus (the agents app): why did the engine pick, or not pick,
 * a value for THIS check and THIS benefit. The worker gives Cyrus the identifiers and nothing
 * else; it reads the data and the code itself. The panel polls until the answer is in.
 *
 * - Nothing asked yet: the button.
 * - QUEUED / RUNNING: a spinner and the usual wait; past SLOW_AFTER_MS a note that the worker may
 *   not be running (there is none on dev).
 * - DONE: the answer, when it was written, and "Ask again" — with a line above it when the
 *   engine's settings changed since it was written (`stale`).
 * - FAILED: why, and a retry.
 *
 * 🚨 The panel tracks the row the POST returned, not just "the newest row": the POST can hand
 * back an older finished answer that is still current, and that is the one to show.
 */
export function EngineExplanation({
  pverifyResponseId,
  benefit,
}: {
  pverifyResponseId: string;
  benefit: ComparisonBenefit;
}) {
  const [requestedId, setRequestedId] = useState<string | null>(null);
  const [latestPolling, setLatestPolling] = useState(0);
  const latest = useGetLatestEngineExplanationQuery(
    { pverifyResponseId, benefit },
    { pollingInterval: requestedId ? 0 : latestPolling }
  );
  const [byIdPolling, setByIdPolling] = useState(POLL_MS);
  const byId = useGetEngineExplanationQuery(requestedId ?? skipToken, {
    pollingInterval: byIdPolling,
  });
  const [ask, { isLoading: isAsking, isError: askFailed }] =
    useRequestEngineExplanationMutation();

  const explanation = requestedId ? byId.data?.data : latest.data?.data;
  const active = isActive(explanation);
  // Poll only while Cyrus is working; stop the moment the row settles.
  useEffect(() => {
    setLatestPolling(!requestedId && active ? POLL_MS : 0);
    setByIdPolling(requestedId && (!explanation || active) ? POLL_MS : 0);
  }, [requestedId, active, explanation]);

  const request = async (rerun: boolean) => {
    try {
      const result = await ask({
        pverifyResponseId,
        benefit,
        ...(rerun ? { rerun: true } : {}),
      }).unwrap();
      setRequestedId(result.data.id);
    } catch {
      // `askFailed` renders the message
    }
  };

  const loading = latest.isLoading || (requestedId !== null && !explanation);

  return (
    <div
      className="border-top-1 surface-border pt-3 mt-3"
      data-testid="benefitComparison.cluster.explanation"
    >
      {askFailed && (
        <Message
          severity="error"
          className="mb-2"
          text="Cyrus could not be asked. Try again."
        />
      )}

      {!explanation && !loading && (
        <Button
          type="button"
          icon="pi pi-comments"
          label={EXPLAIN_BUTTON_LABEL}
          className="p-button-outlined p-button-sm"
          loading={isAsking}
          data-testid="benefitComparison.cluster.explain"
          onClick={() => request(false)}
        />
      )}

      {loading && !explanation && (
        <span className="text-color-secondary">
          <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
          Loading…
        </span>
      )}

      {explanation && active && (
        <div data-testid="benefitComparison.cluster.explanation.working">
          <span>
            <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
            {WORKING_TEXT}
          </span>
          {Date.now() - new Date(explanation.createdAt).getTime() >
            SLOW_AFTER_MS && (
            <p className="text-sm text-color-secondary mt-2 mb-0">
              This is taking longer than usual. If nothing changes, the
              explanation worker may not be running (there is none on dev).
            </p>
          )}
        </div>
      )}

      {explanation?.status === "DONE" && (
        <div data-testid="benefitComparison.cluster.explanation.answer">
          <div className="flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
            <span className="text-xs font-bold uppercase text-color-secondary">
              Cyrus&apos;s explanation
            </span>
            <span
              className="text-sm text-color-secondary"
              title={explanation.finishedAt ?? explanation.createdAt}
            >
              {relativeTime(explanation.finishedAt ?? explanation.createdAt)}
            </span>
          </div>
          {/*
           * 🚨 An answer written under an older configuration can blame a setting that is gone (an
           * ignore keyword since removed). Say so above it rather than let it read as current.
           */}
          {explanation.stale && (
            <p
              className="text-sm text-orange-700 mt-0 mb-2"
              data-testid="benefitComparison.cluster.explanation.stale"
            >
              <i
                className="pi pi-exclamation-triangle mr-2"
                aria-hidden="true"
              />
              {STALE_TEXT}
            </p>
          )}
          <ChatMarkdown text={explanation.answer ?? ""} />
          <Button
            type="button"
            label={ASK_AGAIN_LABEL}
            className="p-button-text p-button-sm px-0 mt-2"
            loading={isAsking}
            data-testid="benefitComparison.cluster.explain.again"
            onClick={() => request(true)}
          />
        </div>
      )}

      {explanation?.status === "FAILED" && (
        <div data-testid="benefitComparison.cluster.explanation.failed">
          <Message
            severity="warn"
            className="mb-2"
            text={`Cyrus could not explain this check: ${
              explanation.error ?? "unknown error"
            }`}
          />
          <Button
            type="button"
            label="Try again"
            className="p-button-outlined p-button-sm"
            loading={isAsking}
            data-testid="benefitComparison.cluster.explain.retry"
            onClick={() => request(true)}
          />
        </div>
      )}
    </div>
  );
}
